import type { ReactNode } from 'react'
import { Anchor, Text } from '@mantine/core'
import { IconExternalLink } from '@tabler/icons-react'
import { cartographerUrl } from '../../utils/cartographerUrl'

/**
 * A hand-off into Cartographer: a real link only when this build knows where
 * Cartographer is.
 *
 * Otherwise the destination is NAMED, not linked — "Cartographer → Governance" —
 * because the path is relative to Cartographer and as a bare `href` it would
 * resolve against Observatory's own origin, which has no such route. A dead
 * link dressed as a hand-off is a fake affordance, and worse than none (doc 00).
 *
 * `label` names the destination in both forms; `children`, when given, replaces
 * the link text (the plain-text form always names the destination by `label`).
 */
export function CartographerLink({
  path, label, children,
}: {
  path: string
  label: string
  children?: ReactNode
}) {
  const url = cartographerUrl(path)

  if (url === null) {
    return (
      <Text component="span" size="sm" c="dimmed">
        Cartographer → {label}
      </Text>
    )
  }

  return (
    <Anchor
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      size="sm"
      aria-label={`Open ${label} in Cartographer`}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
        {children ?? `${label} in Cartographer`}
        <IconExternalLink size={13} />
      </span>
    </Anchor>
  )
}
